import { LinearGradient } from 'expo-linear-gradient';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import Calendar from 'lucide-react-native/icons/calendar';
import Clock from 'lucide-react-native/icons/clock';
import Heart from 'lucide-react-native/icons/heart';
import MapPin from 'lucide-react-native/icons/map-pin';
import Ticket from 'lucide-react-native/icons/ticket';
import { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { CityCrossSell } from '@/components/detail/city-cross-sell';
import { EventHead } from '@/components/detail/event-head';
import { EventRedirect } from '@/components/detail/event-redirect';
import { HotelsMapFrame } from '@/components/detail/hotels-map';
import { EventHotels } from '@/components/detail/hotels-nearby';
import { RelatedList } from '@/components/detail/related-list';
import { ShowDates } from '@/components/detail/show-dates';
import { EventCard } from '@/components/riviera/event-card';
import { FiltersBar } from '@/components/riviera/filters-bar';
import { NoImagePlaceholder } from '@/components/riviera/no-image-placeholder';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { useBrokenImage } from '@/components/riviera/use-broken-image';
import { Badge, BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Fonts, Radius, Spacing } from '@/constants/theme';
import { FiltersProvider } from '@/hooks/use-filters';
import { useEventShow } from '@/hooks/use-event';
import { useRelatedEvents } from '@/hooks/use-related';
import { useTheme } from '@/hooks/use-theme';
import { isEventPast } from '@todo/core/date-range';
import { cities } from '@/lib/events-data';
import { localizedDescription, localizedTitle } from '@todo/core/i18n/event-content';
import { usableImageUrl } from '@todo/core/image-url';
import { currencySymbol, formatDateLong, formatTimeRange } from '@todo/core/i18n/format';
import { useLocale } from '@todo/core/i18n/use-locale';
import { openExternal } from '@todo/core/open-external';
import { type DbEvent } from '@todo/core/supabase';

import css from '@/components/detail/detail-desktop.module.css';

export { generateEventStaticParams as generateStaticParams } from '@/lib/detail/event-detail';

const tagVariant: Record<string, BadgeVariant> = {
  concerts: 'destructive',
  exhibitions: 'primary',
  parties: 'chart4',
  theatre: 'gold',
  gastronomy: 'chart4',
};

type Translate = (key: string, options?: Record<string, unknown>) => string;
type Theme = ReturnType<typeof useTheme>;

function formatPrice(min: number | null, currency: string | null, t: Translate) {
  if (min == null) return t('events.free');
  const symbol = currencySymbol(currency);
  return t('common.fromPrice', { price: `${symbol}${min}` });
}

function MetaList({
  event,
  dateCount,
  location,
  locale,
  theme,
  t,
}: {
  event: DbEvent;
  /** Upcoming dates of the show; with more than one, the header names the next and counts the rest. */
  dateCount: number;
  location: string;
  locale: ReturnType<typeof useLocale>['locale'];
  theme: Theme;
  t: Translate;
}) {
  return (
    <View style={styles.metaList}>
      <View style={styles.metaRow}>
        <Calendar size={16} color={theme.gold} />
        <Text style={[styles.metaText, { color: theme.text }]}>
          {formatDateLong(event.start_ts, event.tz, locale)}
          {dateCount > 1 ? ` · ${t('events.moreDates', { count: dateCount - 1 })}` : ''}
        </Text>
      </View>
      <View style={styles.metaRow}>
        <Clock size={16} color={theme.gold} />
        <Text style={[styles.metaText, { color: theme.text }]}>
          {formatTimeRange(event.start_ts, event.end_ts, event.tz, locale)}
        </Text>
      </View>
      {location ? (
        <View style={styles.metaRow}>
          <MapPin size={16} color={theme.gold} />
          <Text style={[styles.metaText, { color: theme.text }]}>{location}</Text>
        </View>
      ) : null}
      {event.address ? (
        <Text style={[styles.address, { color: theme.mutedForeground }]}>{event.address}</Text>
      ) : null}
    </View>
  );
}

function PriceRow({ event, theme, t }: { event: DbEvent; theme: Theme; t: Translate }) {
  return (
    <View style={styles.priceRow}>
      <Text style={[styles.price, { color: theme.gold }]}>
        {formatPrice(event.min_price, event.currency, t)}
      </Text>
    </View>
  );
}

// Shown in place of TicketButton for events past their 30-day-retention
// window's start (see isEventPast) — the vendor ticket URL is dead by then, so
// this avoids sending a click to a broken affiliate link and funnels toward
// the "more upcoming events" list below instead.
function EndedNotice({ theme, t }: { theme: Theme; t: Translate }) {
  return (
    <View style={[styles.endedNotice, { backgroundColor: theme.muted }]}>
      <Text style={[styles.endedBanner, { color: theme.text }]}>{t('events.endedBanner')}</Text>
      <Text style={[styles.endedHint, { color: theme.mutedForeground }]}>
        {t('events.endedHint')}
      </Text>
    </View>
  );
}

function TicketButton({ event, theme, t }: { event: DbEvent; theme: Theme; t: Translate }) {
  const { locale } = useLocale();
  if (!event.ticket_url) return null;
  return (
    <Button
      variant="gold"
      fullWidth
      style={styles.ticketBtn}
      leadingIcon={<Ticket size={18} color={theme.goldForeground} />}
      onPress={() =>
        openExternal(event.ticket_url as string, {
          kind: 'event',
          source: event.source,
          id: event.id,
          title: localizedTitle(event, locale),
          city: event.city_id,
          locale,
        })
      }>
      {t('events.getTickets')}
    </Button>
  );
}

function Hero({
  event,
  title,
  theme,
  saved,
  onToggleSave,
  onBack,
  t,
}: {
  event: DbEvent;
  title: string;
  theme: Theme;
  saved: boolean;
  onToggleSave: () => void;
  onBack: () => void;
  t: Translate;
}) {
  // AWIN/Fnac feed images are tiny thumbnails (~222x222) with no larger
  // variant available from the source — stretching them full-bleed with
  // `cover` looks blurry/blocky, so letterbox them with `contain` on a muted
  // backdrop instead.
  const lowRes = event.source === 'awin_fnac';
  const { broken, ref, onError } = useBrokenImage();
  const imageUrl = usableImageUrl(event.image_url);

  return (
    <>
      {imageUrl && !broken ? (
        // Raw <img> (not expo-image) so expo's static export bakes the hero —
        // the page's LCP element — into the server-rendered HTML instead of
        // waiting for hydration to insert it. See hero-search.web.tsx.
        <View
          style={[styles.heroFill, lowRes && { backgroundColor: theme.muted }]}>
          <img
            ref={ref}
            src={imageUrl}
            // Localized title, not the raw French feed `event.title`.
            alt={title}
            className={lowRes ? css.heroImgContain : css.heroImg}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            onError={onError}
          />
        </View>
      ) : (
        <View style={styles.heroFill}>
          <NoImagePlaceholder size={120} />
        </View>
      )}
      <LinearGradient colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']} style={styles.heroScrim} />
      <Pressable
        accessibilityLabel={t('a11y.back')}
        onPress={onBack}
        style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
        <ArrowLeft size={20} color="#fff" />
      </Pressable>
      <View style={styles.heroBadge}>
        <Badge variant={tagVariant[event.category_id ?? ''] ?? 'primary'}>
          {event.category_id
            ? t(`categoryLabels.${event.category_id}`, {
                defaultValue: t('categoryLabels.fallback'),
              })
            : t('categoryLabels.fallback')}
        </Badge>
      </View>
      {/* Positioned by CSS, not RN styles: it sits higher on mobile, where
          the content card overlaps the hero's bottom edge. */}
      <div className={css.heroCornerBtn}>
        <Pressable
          accessibilityLabel={saved ? t('a11y.removeFavorite') : t('a11y.addFavorite')}
          onPress={onToggleSave}
          style={({ pressed }) => [
            styles.heartBtn,
            { backgroundColor: theme.card },
            pressed && { opacity: 0.7 },
          ]}>
          <Heart
            size={18}
            color={saved ? theme.destructive : theme.text}
            fill={saved ? theme.destructive : 'transparent'}
          />
        </Pressable>
      </div>
    </>
  );
}

// Web-only: one responsive tree for the mobile and desktop layouts (see the
// single-tree section of detail-desktop.module.css), resolved at first paint
// rather than the native screen's post-mount `isLg` swap. Head + static params
// are shared with the native file.
export default function EventScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { id } = useLocalSearchParams<{ id: string }>();
  // `id` is a show_slug — or an old per-date row id, which redirects.
  const { show, redirectPath, loading, error } = useEventShow(id);
  const [saved, setSaved] = useState(false);
  const [hotelsMapSrc, setHotelsMapSrc] = useState<string | null>(null);

  const event = show?.event ?? null;
  const dates = show?.dates ?? [];

  // A screen reused for another show closes the previous show's hotel map.
  const showSlug = event?.show_slug;
  useEffect(() => setHotelsMapSrc(null), [showSlug]);

  const cityName = event?.city_id
    ? (cities.find((c) => c.id === event.city_id)?.name ?? event.city_id)
    : null;
  const location = event ? [event.venue_name, cityName].filter(Boolean).join(', ') : '';
  const title = event ? localizedTitle(event, locale) : '';
  const descriptionText = event ? localizedDescription(event, locale) : null;
  const isPast = event ? isEventPast(event) : false;

  const { related, relatedSeedKey, seeAllHref, cityId } = useRelatedEvents(event);
  const relatedHeading = cityId
    ? t('events.moreUpcomingCity', { city: cityName })
    : t('events.moreUpcoming');

  const onBack = () => router.back();
  const onToggleSave = () => setSaved((v) => !v);

  if (redirectPath) return <EventRedirect id={id} path={redirectPath} />;

  const description = descriptionText ? (
    // View wrapper matters on web: bare sibling <Text>s default to
    // `display: inline` in react-native-web, so without a block container
    // the heading runs into the body text instead of stacking above it.
    <View>
      <Text
        role="heading"
        aria-level={2}
        style={[styles.sectionHeading, { color: theme.text, fontFamily: Fonts?.serif }]}>
        {t('events.aboutEvent')}
      </Text>
      <Text style={[styles.description, { color: theme.textSecondary }]}>{descriptionText}</Text>
    </View>
  ) : null;

  return (
    <FiltersProvider>
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        {show ? (
          <EventHead
            show={show}
            locale={locale}
            related={related}
            relatedSeedKey={relatedSeedKey}
          />
        ) : null}
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}>
          {event ? (
            <>
              {/* One tree for both breakpoints — mobile: full-bleed hero +
                  overlapping card; desktop: filters sidebar + text-forward
                  two-column content. The regrouping is pure CSS
                  (display: contents — see detail-desktop.module.css), so the
                  title, hero, CTA and description are in the HTML once. */}
              <div className={css.shell}>
                <aside className={css.shellSidebar}>
                  {/* Desktop-only sidebar: skip the chip-scroller markup. */}
                  <FiltersBar variant="desktop" />
                </aside>
                <div className={css.shellMain}>
                  <div className={css.detail}>
                    <div className={css.detailMedia}>
                      <Hero
                        key={event.show_slug}
                        event={event}
                        title={title}
                        theme={theme}
                        saved={saved}
                        onToggleSave={onToggleSave}
                        onBack={onBack}
                        t={t}
                      />
                    </div>

                    <div
                      className={css.detailCard}
                      style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                      <header className={css.detailHeader}>
                        <h1
                          className={css.detailTitle}
                          style={{ color: theme.text, fontFamily: Fonts?.serif }}>
                          {title}
                        </h1>
                        <MetaList
                          event={event}
                          dateCount={dates.length}
                          location={location}
                          locale={locale}
                          theme={theme}
                          t={t}
                        />
                      </header>

                      <div
                        className={css.detailBooking}
                        style={{ backgroundColor: theme.card, borderColor: theme.border }}>
                        <PriceRow event={event} theme={theme} t={t} />
                        {isPast ? (
                          <EndedNotice theme={theme} t={t} />
                        ) : (
                          <TicketButton event={event} theme={theme} t={t} />
                        )}
                        {event.source === 'awin_fnac' ? (
                          <Text style={[styles.attribution, { color: theme.mutedForeground }]}>
                            {t('events.attributionFnac')}
                          </Text>
                        ) : null}
                        {isPast ? null : (
                          <EventHotels
                            key={event.show_slug}
                            event={event}
                            wideMap={{ src: hotelsMapSrc, setSrc: setHotelsMapSrc }}
                          />
                        )}
                      </div>

                      {description || dates.length > 1 ? (
                        <section className={css.detailAbout}>
                          {/* Mobile separates the CTA from the body inside
                              the card; on desktop they're in different
                              columns. */}
                          <div className={css.mobileOnly}>
                            <View style={[styles.divider, { backgroundColor: theme.border }]} />
                          </div>
                          {/* A recurring show lists every date with its own
                              ticket link; the CTA above books the next one. */}
                          {dates.length > 1 ? (
                            <View style={description ? styles.datesBlock : undefined}>
                              <ShowDates event={event} dates={dates} />
                            </View>
                          ) : null}
                          {description}
                        </section>
                      ) : null}
                    </div>
                  </div>
                  {/* The hotel map, once a desktop visitor opens it: the
                      booking column is too narrow for it (see HotelsMap). */}
                  {hotelsMapSrc ? <HotelsMapFrame src={hotelsMapSrc} wide /> : null}
                </div>
              </div>

              {/* "More upcoming events" + tours & boats in the city —
                  bracketed by data-lastmod-skip sentinels:
                  packages/web-build/src/generate-sitemap.js strips this region before
                  hashing the body, so a sibling event or a catalogue change
                  never restamps this page's sitemap lastmod. */}
              <div data-lastmod-skip="start" hidden />
              {related.length > 0 ? (
                <RelatedList heading={relatedHeading} seeAllHref={seeAllHref}>
                  {related.map((e) => (
                    <EventCard key={e.id} event={e} eager={false} />
                  ))}
                </RelatedList>
              ) : null}
              <CityCrossSell cityId={event.city_id} />
              <div data-lastmod-skip="end" hidden />
            </>
          ) : loading ? (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('events.loadingOne')}
            </Text>
          ) : error ? (
            <Text style={[styles.statusText, { color: theme.destructive }]}>
              {t('events.loadError', { error })}
            </Text>
          ) : (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('events.notFound')}
            </Text>
          )}
          <SiteFooter />
        </ScrollView>
      </View>
    </FiltersProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  heroFill: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  heartBtn: {
    width: 40,
    height: 40,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  metaList: {
    marginTop: Spacing.three,
    gap: Spacing.two,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  metaText: {
    fontSize: 15,
    flex: 1,
  },
  address: {
    fontSize: 13,
    marginLeft: 24,
  },
  priceRow: {
    marginBottom: Spacing.one,
  },
  price: {
    fontSize: 22,
    fontWeight: '700',
  },
  ticketBtn: {
    marginTop: Spacing.three,
  },
  endedNotice: {
    marginTop: Spacing.three,
    borderRadius: Radius.lg,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.two,
    gap: 4,
  },
  endedBanner: {
    fontSize: 15,
    fontWeight: '700',
  },
  endedHint: {
    fontSize: 13,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  datesBlock: {
    marginBottom: Spacing.four,
  },
  sectionHeading: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: Spacing.two,
  },
  description: {
    fontSize: 15,
    lineHeight: 24,
  },
  attribution: {
    fontSize: 12,
    marginTop: Spacing.two,
  },
});
